import {
  Alert,
  AlertDescription,
  AlertIcon,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  useToast,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { useContext, useMemo, useState } from "react";
import NumberFormat from "react-number-format";
import { Link, useLocation, useNavigate } from "react-router";

import CommonStockForm from "@equidefi/portals/components/investments/CommonStockForm";
import { VaultContext } from "@equidefi/portals/context/VaultProvider";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useSubmitSubscription } from "@equidefi/portals/hooks/useInvestments";
import { offeringIsRegD, offeringIsTypeRegA } from "@equidefi/shared";
import { Heading, Input, Text } from "@equidefi/ui";
import RadioGroup from "@equidefi/ui/forms/RadioGroup";

import ConvertibleForm from "@/components/subscription/ConvertibleForm";
import DocumentReview from "@/components/subscription/DocumentReview";
import { isConvertibleOffering, isRegD506COffering } from "@/helpers";
import useWorkflowPosition from "@/hooks/useWorkflowPosition";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

import { useUserInvestments } from "@/hooks/useInvestments";
import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";
import WorkflowStepError from "../WorkflowStepError";
import { subscriptionSchema } from "./schema";

const YES_NO_OPTIONS = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

const toBooleanString = (value) => {
  if (value === true || value === false) {
    return String(value);
  }

  return null;
};

export const WorkflowSubscription = () => {
  const { offering, investment, refetchInvestment } = useWorkflowContext();
  const { vault } = useContext(VaultContext);

  const location = useLocation();
  useWorkflowPosition(2);
  const toast = useToast();

  const [spinner, showSpinner] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  useDocumentTitle(["Subscription", offering?.name, investment?.issuer?.name]);
  const navigate = useNavigate();
  const submitSubscription = useSubmitSubscription(investment?.id);

  const unitPrice = Number(offering?.unit_price?.replace(/[^0-9.-]+/g, ""));
  const isOfferingRegA = offeringIsTypeRegA(offering);
  const isOfferingRegD = offeringIsRegD(offering);

  const validationSchema = useMemo(() => {
    return subscriptionSchema({
      offering,
      isOfferingRegA,
      investments: vault?.investments.filter(({ id }) => id !== investment?.id),
    });
  }, [offering, isOfferingRegA, vault, investment]);

  const isExpedited = investment?.expedited;
  const isDefaultable = useMemo(
    () => Number(investment?.amount) > 0 || isExpedited,
    [investment, isExpedited],
  );

  const [acknowledged, setAcknowledged] = useState(
    !!location.state?.returnTo ?? false,
  );
  const isIndividualOrJoint = ["INDIVIDUAL", "JOINT"].includes(
    investment.vault?.ta_account_type,
  );

  const initialValues = {
    accredited: isDefaultable
      ? toBooleanString(investment?.vault.accredited)
      : null,
    broker_dealer: isDefaultable
      ? toBooleanString(investment?.vault?.broker_dealer)
      : null,
    annual_income:
      investment?.vault?.annual_income || isOfferingRegA
        ? isDefaultable
          ? investment?.vault?.annual_income || 0
          : 0
        : null,
    net_worth: isOfferingRegA
      ? isDefaultable
        ? investment?.vault?.net_worth || 0
        : 0
      : null,
    quantity: investment?.quantity || 0,
    amount: investment?.quantity * unitPrice || 0,
  };

  const submitForm = async ({
    amount,
    quantity,
    accredited,
    annual_income,
    net_worth,
    broker_dealer,
  }) => {
    showSpinner(true);

    try {
      let page = "payment";

      const differentValues =
        Number(initialValues?.amount) > 0 &&
        Number(initialValues?.amount) !== Number(amount);

      if (location.state?.returnTo) {
        if (!differentValues) {
          page = location.state.returnTo;
        }
      }

      await submitSubscription.mutateAsync({
        accredited: accredited === "true",
        broker_dealer: broker_dealer === "true",
        annual_income,
        quantity,
        amount,
        net_worth,
      });

      // Refetch investment data first, then navigate
      await refetchInvestment();

      // Mark as navigating to prevent UI flashing during navigation
      setIsNavigating(true);

      navigate(`/offerings/${offering.slug}/${investment?.id}/${page}`, {
        state: {
          returnTo: location.state?.returnTo,
        },
      });
    } catch (err) {
      toast({
        description: "Sorry! Something went wrong updating your subscription",
        isClosable: true,
        status: "error",
      });
      console.error(err);
    } finally {
      showSpinner(false);
    }
  };

  let InvestmentForm = CommonStockForm;
  if (isConvertibleOffering(offering.stock_type)) {
    InvestmentForm = ConvertibleForm;
  }

  const {
    handleSubmit,
    setFieldValue,
    setValues,
    handleBlur,
    validateForm,
    values,
    errors,
    isValid,
    touched,
  } = useFormik({
    initialValues: initialValues,
    enableReinitialize: !spinner,
    validationSchema: validationSchema,
    onSubmit: submitForm,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
  });

  const isAccredited = values?.accredited === "true";

  // Don't render the main content if we're navigating to prevent flash
  if (isNavigating) {
    return (
      <WorkflowContainer
        title="Investment Amount"
        buttonProps={{ isDisabled: true, isLoading: true }}>
        <div />
      </WorkflowContainer>
    );
  }

  if (!investment) return null;

  if (!investment.payment_editable) {
    return (
      <WorkflowStepError
        title="Investment Amount"
        text="Your subscription has already been entered and a payment has been
          submitted, you can no longer edit the subscription amount for this
          investment"
      />
    );
  }

  const isSubmitDisabled = useMemo(() => {
    if (isOfferingRegA) return !acknowledged || !isValid;

    return !isValid;
  }, [isOfferingRegA, acknowledged, isValid]);

  return (
    <WorkflowContainer
      title="Investment Amount"
      onContinue={handleSubmit}
      buttonProps={{ isDisabled: isSubmitDisabled, isLoading: spinner }}>
      <WorkflowForm onSubmit={handleSubmit}>
        <FormControl mb={2}>
          <FormLabel>
            <Text mb="0">
              Are you an accredited investor?{" "}
              {isOfferingRegA
                ? "If not, don't worry, you can still invest!"
                : ""}
            </Text>
          </FormLabel>
          <Alert
            status="warning"
            borderRadius="lg"
            border="1px solid"
            borderColor="equidefi.yellow"
            boxShadow="sm"
            mb={2}>
            <AlertDescription>
              {isOfferingRegA ? (
                <Text textStyle="body2" mb={0}>
                  Accredited investor means you have a net worth greater than $1
                  million (not including your primary residence) or an income
                  greater than $200,000 in the last two years as an individual,
                  or $300,000 as a joint account. If you are not accredited you
                  can still invest, but as long as the purchase does not exceed
                  10% of the investor's net worth or annual income, whichever is
                  greater.
                </Text>
              ) : (
                <Text textStyle="body2" mb={0}>
                  Accredited investor means you have a net worth greater than $1
                  million (not including your primary residence) or an income
                  greater than $200,000 in the last two years as an individual,
                  or $300,000 as a joint account.
                </Text>
              )}
            </AlertDescription>
          </Alert>

          <RadioGroup
            fieldName="accredited"
            options={YES_NO_OPTIONS}
            value={values?.accredited}
            isDisabled={isExpedited}
            setFieldValue={setFieldValue}
          />
        </FormControl>
        <Text textStyle="context">
          Please see "
          <Text
            textStyle="context"
            as={Link}
            textDecor="underline"
            _hover={{ color: "blue.700" }}
            color="blue.600"
            to="https://www.sec.gov/education/capitalraising/building-blocks/accredited-investor"
            rel="noopener noreferrer"
            target="_blank">
            what is an accredited investor
          </Text>
          " to make sure you make the right selection.
        </Text>

        {!isOfferingRegA && !isAccredited && (
          <Alert
            status="error"
            borderRadius="lg"
            border="1px solid"
            borderColor="equidefi.red"
            boxShadow="sm"
            mb={4}>
            <AlertIcon />
            <Text textStyle="body2" m={0}>
              You must be an accredited investor to invest in this offering.
            </Text>
          </Alert>
        )}

        <FormControl mb={2} mt={10}>
          <FormLabel>
            <Text mb="0">Are you affiliated with a broker dealer?</Text>
          </FormLabel>

          <RadioGroup
            fieldName="broker_dealer"
            options={YES_NO_OPTIONS}
            value={values?.broker_dealer}
            setFieldValue={setFieldValue}
            isDisabled={isExpedited}
          />
        </FormControl>
        <Text textStyle="context" mb={6}>
          Please see "
          <Text
            textStyle="context"
            as={Link}
            textDecor="underline"
            _hover={{ color: "blue.700" }}
            color="blue.600"
            to="https://www.finra.org/investors/learn-to-invest/choosing-investment-professional/brokers"
            rel="noopener noreferrer"
            target="_blank">
            What Is An Affiliate of a Broker Dealer
          </Text>
          " to make sure you make the right selection.
        </Text>

        {isOfferingRegA && (
          <>
            <Input
              label={isIndividualOrJoint ? "Annual Income" : "Revenue"}
              isInvalid={errors.annual_income && touched.annual_income}
              maxW="50%"
              as={NumberFormat}
              allowNegative={false}
              allowLeadingZeros={false}
              thousandSeparator
              fixedDecimalScale={0}
              decimalScale={0}
              prefix="$"
              name="annual_income"
              aria-label={isIndividualOrJoint ? "Annual Income" : "Revenue"}
              value={values.annual_income}
              onValueChange={(...args) => {
                const [{ floatValue }] = args;
                setFieldValue("annual_income", floatValue);
                validateForm();
              }}
              onFocus={() => {
                if (Number(values.annual_income) === 0) {
                  setFieldValue("annual_income", "");
                }
              }}
              error={errors.annual_income}
              isRequired={true}
              onBlur={handleBlur}
              isDisabled={isExpedited}
            />
            <Input
              label={isIndividualOrJoint ? "Net Worth" : "Net Assets"}
              isInvalid={errors.net_worth && touched.net_worth}
              maxW="50%"
              as={NumberFormat}
              allowNegative={false}
              allowLeadingZeros={false}
              thousandSeparator
              fixedDecimalScale={0}
              decimalScale={0}
              prefix="$"
              name="net_worth"
              aria-label={isIndividualOrJoint ? "Net Worth" : "Net Assets"}
              value={values.net_worth}
              onValueChange={(...args) => {
                const [{ floatValue }] = args;
                setFieldValue("net_worth", floatValue);
                validateForm();
              }}
              onFocus={() => {
                if (Number(values.net_worth) === 0) {
                  setFieldValue("net_worth", "");
                }
              }}
              error={errors.net_worth}
              isRequired={true}
              onBlur={handleBlur}
              isDisabled={isExpedited}
            />

            <Text textStyle="context" mb={3}>
              For non-accredited investors, investments are limited to a maximum
              of 10% of net worth or annual income, whichever is greater, if the
              Investor is a natural person; or 10% of its revenues or net
              assets, whichever is greater, for its most recently completed
              fiscal year, if the Investor is a non-natural person.
            </Text>

            <FormControl mb="4" isInvalid={touched.quantity && !acknowledged}>
              <Checkbox
                isChecked={acknowledged}
                isRequired
                name="acknowledged"
                onChange={() => setAcknowledged((prev) => !prev)}>
                I acknowledge that I am self-certifying these values.
              </Checkbox>
              <FormErrorMessage>Required</FormErrorMessage>
            </FormControl>
          </>
        )}

        {((isAccredited && isOfferingRegD) || isOfferingRegA) && (
          <>
            <Heading textStyle="h1" fontWeight="bold" mb={0}>
              Amount
            </Heading>
            <Text>Select the amount you want to invest</Text>
            <InvestmentForm
              values={values}
              handleBlur={handleBlur}
              setValues={setValues}
              errors={errors}
              touched={touched}
              offering={offering}
            />
          </>
        )}
        <DocumentReview documents={offering.documents} />
      </WorkflowForm>
    </WorkflowContainer>
  );
};
